import axios from 'axios';
import { globalCache, generateCacheKey } from '../utils/smartCache';
import { taxiStandsCircuitBreaker } from '../utils/circuitBreaker';
import { measureApiCall } from '../utils/performanceMonitor';
import { offlineManager } from '../utils/offlineManager';

const TAXI_STANDS_WFS_URL =
    'https://portal.csdi.gov.hk/server/services/common/td_rcd_1697081907714_17556/MapServer/WFSServer';
// The dataset has ~518 stands; fetching fewer returns an arbitrary subset (by ID), not nearest ones.
const MAX_TAXI_STANDS_FEATURES = 1000;

// Coordinate system conversion utilities
export interface CoordinateSystem {
    epsg: string;
    name: string;
}

export const COORDINATE_SYSTEMS = {
    WGS84: { epsg: 'EPSG:4326', name: 'WGS84' },
    HK1980: { epsg: 'EPSG:2326', name: 'Hong Kong 1980 Grid' }
} as const;

export interface BoundingBox {
    minLat: number;
    minLon: number;
    maxLat: number;
    maxLon: number;
}

export interface SpatialFilter {
    boundingBox: BoundingBox;
    coordinateSystem: CoordinateSystem;
    maxFeatures?: number;
}

export interface TaxiStand {
    id: string;
    location: {
        latitude: number;
        longitude: number;
    };
    region: {
        en: string;
        tc: string;
        sc: string;
    };
    district: {
        en: string;
        tc: string;
        sc: string;
    };
    locationName: {
        en: string;
        tc: string;
        sc: string;
    };
    status: {
        en: string;
        tc: string;
        sc: string;
    };
    distance?: number; // Distance from user's location in meters
}

export interface TaxiStandsResponse {
    success: boolean;
    data: TaxiStand[];
    error?: string;
}

// Cache for taxi stands data
let taxiStandsCache: TaxiStand[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Utility functions for coordinate conversion and spatial operations
export const createBoundingBox = (
    centerLat: number, 
    centerLon: number, 
    radiusKm: number
): BoundingBox => {
    const earthRadius = 6371; // Earth's radius in km
    const latDelta = (radiusKm / earthRadius) * (180 / Math.PI);
    const lonDelta = (radiusKm / earthRadius) * (180 / Math.PI) / Math.cos(centerLat * Math.PI / 180);
    
    return {
        minLat: centerLat - latDelta,
        minLon: centerLon - lonDelta,
        maxLat: centerLat + latDelta,
        maxLon: centerLon + lonDelta
    };
};

export const createSpatialFilter = (
    centerLat: number,
    centerLon: number,
    radiusKm: number = 2, // Default 2km radius
    coordinateSystem: CoordinateSystem = COORDINATE_SYSTEMS.WGS84,
    maxFeatures: number = 50
): SpatialFilter => {
    return {
        boundingBox: createBoundingBox(centerLat, centerLon, radiusKm),
        coordinateSystem,
        maxFeatures
    };
};

// Convert WGS84 coordinates to Hong Kong 1980 Grid (EPSG:2326)
// This is a simplified conversion - for production use, consider using a proper coordinate transformation library
export const convertToHK1980 = (lat: number, lon: number): { x: number; y: number } => {
    // Simplified conversion - in production, use proper transformation parameters
    // This is an approximation for Hong Kong area
    const hkOriginLat = 22.302711;
    const hkOriginLon = 114.177216;
    
    // Rough conversion factors (these would need to be calibrated for accuracy)
    const x = (lon - hkOriginLon) * 111320 * Math.cos(lat * Math.PI / 180) + 800000;
    const y = (lat - hkOriginLat) * 110540 + 800000;
    
    return { x: Math.round(x), y: Math.round(y) };
};

// Convert Hong Kong 1980 Grid back to WGS84
export const convertFromHK1980 = (x: number, y: number): { lat: number; lon: number } => {
    const hkOriginLat = 22.302711;
    const hkOriginLon = 114.177216;
    
    const lat = hkOriginLat + (y - 800000) / 110540;
    const lon = hkOriginLon + (x - 800000) / (111320 * Math.cos(lat * Math.PI / 180));
    
    return { lat, lon };
};

// Calculate distance between two points using Haversine formula
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) *
        Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
};

// Generate GML envelope for spatial filtering
const generateGMLEnvelope = (boundingBox: BoundingBox, coordinateSystem: CoordinateSystem): string => {
    if (coordinateSystem.epsg === 'EPSG:2326') {
        // Convert WGS84 bounding box to HK1980 coordinates
        const minHK = convertToHK1980(boundingBox.minLat, boundingBox.minLon);
        const maxHK = convertToHK1980(boundingBox.maxLat, boundingBox.maxLon);
        
        return `<gml:Envelope srsName='${coordinateSystem.epsg}'>
            <gml:lowerCorner>${minHK.x} ${minHK.y}</gml:lowerCorner>
            <gml:upperCorner>${maxHK.x} ${maxHK.y}</gml:upperCorner>
        </gml:Envelope>`;
    } else {
        // For WGS84, use lat/lon directly
        return `<gml:Envelope srsName='${coordinateSystem.epsg}'>
            <gml:lowerCorner>${boundingBox.minLon} ${boundingBox.minLat}</gml:lowerCorner>
            <gml:upperCorner>${boundingBox.maxLon} ${boundingBox.maxLat}</gml:upperCorner>
        </gml:Envelope>`;
    }
};

// Generate spatial filter XML
const generateSpatialFilter = (spatialFilter: SpatialFilter): string => {
    const envelope = generateGMLEnvelope(spatialFilter.boundingBox, spatialFilter.coordinateSystem);
    
    return `<Filter>
        <Intersects>
            <PropertyName>SHAPE</PropertyName>
            ${envelope}
        </Intersects>
    </Filter>`;
};

// Fetch taxi stands data with spatial filtering
export const fetchTaxiStandsWithSpatialFilter = async (
    spatialFilter: SpatialFilter,
    userLat?: number,
    userLon?: number
): Promise<TaxiStandsResponse> => {
    try {
        const filter = generateSpatialFilter(spatialFilter);
        
        console.log('🔍 Spatial Filter Debug Info:');
        console.log('================================');
        console.log('📍 User Location:', { lat: userLat, lon: userLon });
        console.log('📦 Bounding Box:', spatialFilter.boundingBox);
        console.log('🎯 Coordinate System:', spatialFilter.coordinateSystem);
        console.log('📊 Max Features:', spatialFilter.maxFeatures);
        console.log('🔧 Generated Filter:', filter);
        
        const response = await axios.get(TAXI_STANDS_WFS_URL, {
            params: {
                service: 'wfs',
                request: 'GetFeature',
                typenames: 'Taxi_Stands',
                outputFormat: 'geojson',
                maxFeatures: spatialFilter.maxFeatures || MAX_TAXI_STANDS_FEATURES,
                srsName: spatialFilter.coordinateSystem.epsg,
                filter: filter
            },
            timeout: 15000
        });

        console.log('📡 API Response Status:', response.status);
        console.log('📄 Response Data:', response.data);
        console.log('🔢 Features Count:', response.data?.features?.length || 0);

        if (response.data && response.data.features) {
            const taxiStands: TaxiStand[] = response.data.features.map((feature: any) => {
                let lat, lon;
                
                if (spatialFilter.coordinateSystem.epsg === 'EPSG:2326') {
                    // Convert from HK1980 to WGS84
                    const coords = convertFromHK1980(
                        feature.geometry.coordinates[0],
                        feature.geometry.coordinates[1]
                    );
                    lat = coords.lat;
                    lon = coords.lon;
                } else {
                    // Already in WGS84
                    lat = feature.geometry.coordinates[1];
                    lon = feature.geometry.coordinates[0];
                }

                return {
                    id: feature.properties.GmlID || feature.properties.OBJECTID?.toString(),
                    location: { latitude: lat, longitude: lon },
                    region: {
                        en: feature.properties.Region_EN || '',
                        tc: feature.properties.Region_TC || '',
                        sc: feature.properties.Region_SC || ''
                    },
                    district: {
                        en: feature.properties.District_EN || '',
                        tc: feature.properties.District_TC || '',
                        sc: feature.properties.District_SC || ''
                    },
                    locationName: {
                        en: feature.properties.Location_EN || '',
                        tc: feature.properties.Location_TC || '',
                        sc: feature.properties.Location_SC || ''
                    },
                    status: {
                        en: feature.properties.Status_EN || '',
                        tc: feature.properties.Status_TC || '',
                        sc: feature.properties.Status_SC || ''
                    }
                };
            });

            // Calculate distances and sort if user location is provided
            if (userLat !== undefined && userLon !== undefined) {
                const standsWithDistance = taxiStands.map(stand => ({
                    ...stand,
                    distance: calculateDistance(userLat, userLon, stand.location.latitude, stand.location.longitude)
                })).sort((a, b) => (a.distance || 0) - (b.distance || 0));

                return {
                    success: true,
                    data: standsWithDistance
                };
            }

            return {
                success: true,
                data: taxiStands
            };
        }

        return {
            success: false,
            data: [],
            error: 'Invalid response format from taxi stands API'
        };

    } catch (error) {
        console.error('❌ Error fetching taxi stands with spatial filter:', error);
        console.log('🔄 Attempting fallback to basic API...');
        
        // Fallback to basic API without spatial filtering
        try {
            const fallbackResponse = await axios.get(TAXI_STANDS_WFS_URL, {
                params: {
                    service: 'wfs',
                    request: 'GetFeature',
                    typenames: 'Taxi_Stands',
                    outputFormat: 'geojson',
                    maxFeatures: MAX_TAXI_STANDS_FEATURES,
                    srsName: 'EPSG:4326'
                },
                timeout: 10000
            });

            console.log('🔄 Fallback API Response:', fallbackResponse.data);
            
            if (fallbackResponse.data && fallbackResponse.data.features) {
                const taxiStands: TaxiStand[] = fallbackResponse.data.features.map((feature: any) => ({
                    id: feature.properties.GmlID || feature.properties.OBJECTID?.toString(),
                    location: {
                        latitude: feature.geometry.coordinates[1],
                        longitude: feature.geometry.coordinates[0]
                    },
                    region: {
                        en: feature.properties.Region_EN || '',
                        tc: feature.properties.Region_TC || '',
                        sc: feature.properties.Region_SC || ''
                    },
                    district: {
                        en: feature.properties.District_EN || '',
                        tc: feature.properties.District_TC || '',
                        sc: feature.properties.District_SC || ''
                    },
                    locationName: {
                        en: feature.properties.Location_EN || '',
                        tc: feature.properties.Location_TC || '',
                        sc: feature.properties.Location_SC || ''
                    },
                    status: {
                        en: feature.properties.Status_EN || '',
                        tc: feature.properties.Status_TC || '',
                        sc: feature.properties.Status_SC || ''
                    }
                }));

                // Calculate distances and sort if user location is provided
                if (userLat !== undefined && userLon !== undefined) {
                    const standsWithDistance = taxiStands.map(stand => ({
                        ...stand,
                        distance: calculateDistance(userLat, userLon, stand.location.latitude, stand.location.longitude)
                    })).sort((a, b) => (a.distance || 0) - (b.distance || 0));

                    return {
                        success: true,
                        data: standsWithDistance,
                        error: 'Using fallback API due to spatial filter error'
                    };
                }

                return {
                    success: true,
                    data: taxiStands,
                    error: 'Using fallback API due to spatial filter error'
                };
            }
        } catch (fallbackError) {
            console.error('❌ Fallback API also failed:', fallbackError);
        }
        
        return {
            success: false,
            data: [],
            error: error instanceof Error ? error.message : 'Failed to fetch taxi stands'
        };
    }
};

// Legacy function for backward compatibility
export const fetchTaxiStands = async (userLat?: number, userLon?: number): Promise<TaxiStandsResponse> => {
    try {
        // Check cache first
        const now = Date.now();
        if (taxiStandsCache && (now - cacheTimestamp) < CACHE_DURATION) {
            let cachedData = [...taxiStandsCache];
            
            // If user location is provided, calculate distances and sort
            if (userLat !== undefined && userLon !== undefined) {
                cachedData = cachedData.map(stand => ({
                    ...stand,
                    distance: calculateDistance(userLat, userLon, stand.location.latitude, stand.location.longitude)
                })).sort((a, b) => (a.distance || 0) - (b.distance || 0));
            }
            
            return {
                success: true,
                data: cachedData
            };
        }

        // Fetch fresh data from API
        const response = await axios.get(TAXI_STANDS_WFS_URL, {
            params: {
                service: 'wfs',
                request: 'GetFeature',
                typenames: 'Taxi_Stands',
                outputFormat: 'geojson',
                maxFeatures: MAX_TAXI_STANDS_FEATURES,
                srsName: 'EPSG:4326'
            },
            timeout: 10000
        });

        if (response.data && response.data.features) {
            const taxiStands: TaxiStand[] = response.data.features.map((feature: any) => ({
                id: feature.properties.GmlID || feature.properties.OBJECTID?.toString(),
                location: {
                    latitude: feature.geometry.coordinates[1],
                    longitude: feature.geometry.coordinates[0]
                },
                region: {
                    en: feature.properties.Region_EN || '',
                    tc: feature.properties.Region_TC || '',
                    sc: feature.properties.Region_SC || ''
                },
                district: {
                    en: feature.properties.District_EN || '',
                    tc: feature.properties.District_TC || '',
                    sc: feature.properties.District_SC || ''
                },
                locationName: {
                    en: feature.properties.Location_EN || '',
                    tc: feature.properties.Location_TC || '',
                    sc: feature.properties.Location_SC || ''
                },
                status: {
                    en: feature.properties.Status_EN || '',
                    tc: feature.properties.Status_TC || '',
                    sc: feature.properties.Status_SC || ''
                }
            }));

            // Update cache
            taxiStandsCache = taxiStands;
            cacheTimestamp = now;

            // If user location is provided, calculate distances and sort
            if (userLat !== undefined && userLon !== undefined) {
                const standsWithDistance = taxiStands.map(stand => ({
                    ...stand,
                    distance: calculateDistance(userLat, userLon, stand.location.latitude, stand.location.longitude)
                })).sort((a, b) => (a.distance || 0) - (b.distance || 0));

                return {
                    success: true,
                    data: standsWithDistance
                };
            }

            return {
                success: true,
                data: taxiStands
            };
        }

        return {
            success: false,
            data: [],
            error: 'Invalid response format from taxi stands API'
        };

    } catch (error) {
        console.error('Error fetching taxi stands:', error);
        
        // Return cached data if available, even if expired
        if (taxiStandsCache) {
            let cachedData = [...taxiStandsCache];
            
            if (userLat !== undefined && userLon !== undefined) {
                cachedData = cachedData.map(stand => ({
                    ...stand,
                    distance: calculateDistance(userLat, userLon, stand.location.latitude, stand.location.longitude)
                })).sort((a, b) => (a.distance || 0) - (b.distance || 0));
            }
            
            return {
                success: true,
                data: cachedData,
                error: 'Using cached data due to API error'
            };
        }

        return {
            success: false,
            data: [],
            error: error instanceof Error ? error.message : 'Failed to fetch taxi stands'
        };
    }
};

// Automatic spatial calculation and taxi stands fetching
export const getNearbyTaxiStandsAuto = async (
    userLat: number,
    userLon: number,
    options: {
        radiusKm?: number;
        maxFeatures?: number;
        coordinateSystem?: CoordinateSystem;
        useSmartRadius?: boolean;
    } = {}
): Promise<TaxiStandsResponse> => {
    const {
        radiusKm = 2,
        maxFeatures = 20,
        coordinateSystem = COORDINATE_SYSTEMS.HK1980,
        useSmartRadius = true
    } = options;

    // Calculate optimal radius based on location density
    const smartRadius = useSmartRadius ? calculateOptimalRadius(userLat, userLon) : radiusKm;
    
    // Create spatial filter with calculated parameters
    const spatialFilter = createSpatialFilter(
        userLat,
        userLon,
        smartRadius,
        coordinateSystem,
        maxFeatures
    );

    console.log(`🔍 Searching taxi stands within ${smartRadius}km of location (${userLat}, ${userLon})`);
    console.log(`📐 Using coordinate system: ${coordinateSystem.name}`);
    console.log(`🎯 Max features: ${maxFeatures}`);

    return await fetchTaxiStandsWithSpatialFilter(spatialFilter, userLat, userLon);
};

// Calculate optimal search radius based on location characteristics
const calculateOptimalRadius = (lat: number, lon: number): number => {
    // Hong Kong specific location analysis with proper area detection
    const isCentralHK = lat >= 22.25 && lat <= 22.35 && lon >= 114.1 && lon <= 114.2;
    const isKowloon = lat >= 22.25 && lat <= 22.35 && lon >= 114.1 && lon <= 114.2;
    const isHongKongIsland = lat >= 22.25 && lat <= 22.35 && lon >= 114.1 && lon <= 114.2;
    const isAirport = lat >= 22.3 && lat <= 22.33 && lon >= 113.9 && lon <= 114.0;
    const isKwunTong = lat >= 22.31 && lat <= 22.33 && lon >= 114.2 && lon <= 114.25;
    const isTseungKwanO = lat >= 22.25 && lat <= 22.35 && lon >= 114.25 && lon <= 114.35;
    const isNewTerritories = lat < 22.25 || lat > 22.35 || lon < 114.1 || lon > 114.25;
    
    if (isCentralHK) {
        // Central Hong Kong - dense taxi stands, smaller radius
        return 1.5;
    } else if (isKwunTong) {
        // Kwun Tong - industrial area with moderate taxi stands
        return 2.5;
    } else if (isTseungKwanO) {
        // Tseung Kwan O - newer area, medium radius
        return 3.0;
    } else if (isAirport) {
        // Airport area - specific taxi stands, medium radius
        return 3.0;
    } else if (isKowloon) {
        // Kowloon area - urban with good taxi coverage
        return 2.0;
    } else if (isHongKongIsland) {
        // Hong Kong Island - good taxi coverage
        return 2.0;
    } else if (isNewTerritories) {
        // New Territories - fewer taxi stands, larger radius
        return 4.0;
    } else {
        // Default radius for any other location
        return 2.5;
    }
};

// Get taxi stands with automatic spatial calculation and smart parameters
export const getNearbyTaxiStandsSmart = async (
    userLat: number,
    userLon: number
): Promise<TaxiStandsResponse> => {
    return await getNearbyTaxiStandsAuto(userLat, userLon, {
        useSmartRadius: true,
        maxFeatures: 15,
        coordinateSystem: COORDINATE_SYSTEMS.HK1980
    });
};

// Get very nearest taxi stands within 0.5km with enhanced proximity analysis
export const getVeryNearestTaxiStands = async (
    userLat: number,
    userLon: number,
    maxDistance: number = 500 // 0.5km default for very nearest stands
): Promise<TaxiStandsResponse> => {
    console.log('🎯 Finding Very Nearest Taxi Stands (Within 0.5km)');
    console.log('==================================================');
    console.log(`📍 User Location: (${userLat}, ${userLon})`);
    console.log(`📏 Max Distance: ${maxDistance}m (0.5km)`);
    
    // Generate cache key for this specific search
    const cacheKey = generateCacheKey('very_nearest_stands', { userLat, userLon, maxDistance });
    
    // Check cache first
    const cached = globalCache.get<TaxiStandsResponse>(cacheKey);
    if (cached) {
        console.log('📦 Using cached taxi stands data');
        return cached;
    }
    
    try {
        // Check if we're offline and have cached data
        if (!offlineManager.isAppOnline()) {
            console.log('📱 App is offline, checking cached data...');
            const offlineStands = await offlineManager.getTaxiStandsByLocation(userLat, userLon, maxDistance * 2);
            if (offlineStands.length > 0) {
                console.log(`📱 Found ${offlineStands.length} cached taxi stands offline`);
                return {
                    success: true,
                    data: offlineStands.slice(0, 10)
                };
            }
        }
        
        // Use circuit breaker for API calls
        const apiCall = async () => {
            console.log('🔄 Step 1: Fetching all taxi stands...');
            return await axios.get(TAXI_STANDS_WFS_URL, {
                params: {
                    service: 'wfs',
                    request: 'GetFeature',
                    typenames: 'Taxi_Stands',
                    outputFormat: 'geojson',
                    maxFeatures: MAX_TAXI_STANDS_FEATURES,
                    srsName: 'EPSG:4326'
                },
                timeout: 15000
            });
        };
        
        const fallbackCall = async () => {
            console.log('🔄 Fallback: Using offline data...');
            const offlineStands = await offlineManager.getTaxiStandsByLocation(userLat, userLon, maxDistance * 2);
            return {
                status: 200,
                statusText: 'OK',
                headers: {},
                config: {},
                data: {
                    features: offlineStands.map(stand => ({
                        properties: {
                            GmlID: stand.id,
                            Region_EN: stand.region.en,
                            Region_TC: stand.region.tc,
                            Region_SC: stand.region.sc,
                            District_EN: stand.district.en,
                            District_TC: stand.district.tc,
                            District_SC: stand.district.sc,
                            Location_EN: stand.locationName.en,
                            Location_TC: stand.locationName.tc,
                            Location_SC: stand.locationName.sc,
                            Status_EN: stand.status.en,
                            Status_TC: stand.status.tc,
                            Status_SC: stand.status.sc
                        },
                        geometry: {
                            coordinates: [stand.location.longitude, stand.location.latitude]
                        }
                    }))
                }
            } as any;
        };
        
        // Measure API call performance
        const basicResponse = await measureApiCall(
            'fetch_taxi_stands',
            () => taxiStandsCircuitBreaker.execute(apiCall, fallbackCall),
            { userLat, userLon, maxDistance }
        );

        console.log('📡 Basic API Response Status:', basicResponse.status);
        console.log('🔢 Total Features Found:', basicResponse.data?.features?.length || 0);

        if (basicResponse.data && basicResponse.data.features) {
            // Convert all stands and calculate distances
            const allStands: TaxiStand[] = basicResponse.data.features.map((feature: any) => {
                const lat = feature.geometry.coordinates[1];
                const lon = feature.geometry.coordinates[0];
                const distance = calculateDistance(userLat, userLon, lat, lon);

                return {
                    id: feature.properties.GmlID || feature.properties.OBJECTID?.toString(),
                    location: { latitude: lat, longitude: lon },
                    region: {
                        en: feature.properties.Region_EN || '',
                        tc: feature.properties.Region_TC || '',
                        sc: feature.properties.Region_SC || ''
                    },
                    district: {
                        en: feature.properties.District_EN || '',
                        tc: feature.properties.District_TC || '',
                        sc: feature.properties.District_SC || ''
                    },
                    locationName: {
                        en: feature.properties.Location_EN || '',
                        tc: feature.properties.Location_TC || '',
                        sc: feature.properties.Location_SC || ''
                    },
                    status: {
                        en: feature.properties.Status_EN || '',
                        tc: feature.properties.Status_TC || '',
                        sc: feature.properties.Status_SC || ''
                    },
                    distance: distance
                };
            });

            console.log(`📊 Processed ${allStands.length} taxi stands`);

            // Filter stands within 500m and sort by distance
            const nearestStands = allStands
                .filter(stand => (stand.distance || 0) <= maxDistance)
                .sort((a, b) => (a.distance || 0) - (b.distance || 0))
                .slice(0, 10); // Get top 10 nearest within 500m

            console.log(`✅ Found ${nearestStands.length} stands within ${maxDistance}m`);

            if (nearestStands.length > 0) {
                // Log detailed proximity info
                nearestStands.forEach((stand, index) => {
                    const proximity = analyzeProximity(stand, userLat, userLon);
                    console.log(`🏃 Stand ${index + 1}: ${stand.locationName.en} - ${proximity.recommendation} (${Math.round(stand.distance || 0)}m)`);
                });
                
                const result = {
                    success: true,
                    data: nearestStands
                };
                
                // Cache the result
                globalCache.set(cacheKey, result, 300000); // 5 minutes
                
                // Save to offline storage
                offlineManager.saveTaxiStands(nearestStands);
                
                return result;
            } else {
                // If no stands within 500m, get the 10 closest stands regardless of distance
                console.log('⚠️ No stands within 500m, getting closest 10 stands...');
                const closestStands = allStands
                    .sort((a, b) => (a.distance || 0) - (b.distance || 0))
                    .slice(0, 10);

                console.log(`✅ Found ${closestStands.length} closest stands (closest: ${Math.round(closestStands[0]?.distance || 0)}m)`);
                
                const result = {
                    success: true,
                    data: closestStands
                };
                
                // Cache the result
                globalCache.set(cacheKey, result, 300000); // 5 minutes
                
                // Save to offline storage
                offlineManager.saveTaxiStands(closestStands);
                
                return result;
            }
        }

        return {
            success: false,
            data: [],
            error: 'No taxi stands found in the API response'
        };

    } catch (error) {
        console.error('❌ Error finding very nearest stands:', error);
        return {
            success: false,
            data: [],
            error: error instanceof Error ? error.message : 'Failed to find nearest stands'
        };
    }
};

// Enhanced proximity analysis with walking time estimation
export const analyzeProximity = (stand: TaxiStand, userLat: number, userLon: number): {
    distance: number;
    walkingTime: number;
    proximityLevel: 'immediate' | 'nearby' | 'walkable' | 'distant';
    recommendation: string;
} => {
    const distance = stand.distance || calculateDistance(userLat, userLon, stand.location.latitude, stand.location.longitude);
    const walkingTime = Math.round(distance / 80); // 80m/min average walking speed
    
    let proximityLevel: 'immediate' | 'nearby' | 'walkable' | 'distant';
    let recommendation: string;
    
    if (distance <= 200) {
        proximityLevel = 'immediate';
        recommendation = 'Very close - 2-3 minute walk';
    } else if (distance <= 500) {
        proximityLevel = 'nearby';
        recommendation = 'Close - 5-7 minute walk';
    } else if (distance <= 1000) {
        proximityLevel = 'walkable';
        recommendation = 'Walkable - 10-15 minute walk';
    } else {
        proximityLevel = 'distant';
        recommendation = 'Consider taking transport to reach this stand';
    }
    
    return {
        distance,
        walkingTime,
        proximityLevel,
        recommendation
    };
};

// Optimized function to get nearby taxi stands with spatial filtering
export const getNearbyTaxiStandsOptimized = async (
    userLat: number,
    userLon: number,
    radiusKm: number = 2,
    coordinateSystem: CoordinateSystem = COORDINATE_SYSTEMS.HK1980,
    maxFeatures: number = 20
): Promise<TaxiStandsResponse> => {
    const spatialFilter = createSpatialFilter(
        userLat,
        userLon,
        radiusKm,
        coordinateSystem,
        maxFeatures
    );
    
    return await fetchTaxiStandsWithSpatialFilter(spatialFilter, userLat, userLon);
};

// Get nearby taxi stands within a specified radius (legacy function)
export const getNearbyTaxiStands = async (
    userLat: number, 
    userLon: number, 
    radiusMeters: number = 1000
): Promise<TaxiStandsResponse> => {
    const response = await fetchTaxiStands(userLat, userLon);
    
    if (response.success) {
        const nearbyStands = response.data.filter(stand => 
            (stand.distance || 0) <= radiusMeters
        );
        
        return {
            success: true,
            data: nearbyStands
        };
    }
    
    return response;
};

// Location-based utilities for automatic spatial calculation
export const getLocationContext = (lat: number, lon: number): {
    area: string;
    density: 'high' | 'medium' | 'low';
    recommendedRadius: number;
    description: string;
} => {
    // Hong Kong area analysis with proper geographic boundaries
    const isCentralHK = lat >= 22.25 && lat <= 22.35 && lon >= 114.1 && lon <= 114.2;
    const isKwunTong = lat >= 22.31 && lat <= 22.33 && lon >= 114.2 && lon <= 114.25;
    const isTseungKwanO = lat >= 22.25 && lat <= 22.35 && lon >= 114.25 && lon <= 114.35;
    const isAirport = lat >= 22.3 && lat <= 22.33 && lon >= 113.9 && lon <= 114.0;
    const isKowloon = lat >= 22.25 && lat <= 22.35 && lon >= 114.1 && lon <= 114.2;
    const isHongKongIsland = lat >= 22.25 && lat <= 22.35 && lon >= 114.1 && lon <= 114.2;
    const isNewTerritories = lat < 22.25 || lat > 22.35 || lon < 114.1 || lon > 114.25;
    
    if (isCentralHK) {
        return {
            area: 'Central Hong Kong',
            density: 'high',
            recommendedRadius: 1.5,
            description: 'Dense urban area with many taxi stands nearby'
        };
    } else if (isKwunTong) {
        return {
            area: 'Kwun Tong',
            density: 'medium',
            recommendedRadius: 2.5,
            description: 'Industrial area with moderate taxi stand coverage'
        };
    } else if (isTseungKwanO) {
        return {
            area: 'Tseung Kwan O',
            density: 'medium',
            recommendedRadius: 3.0,
            description: 'Newer residential area with good taxi stand coverage'
        };
    } else if (isAirport) {
        return {
            area: 'Hong Kong International Airport',
            density: 'medium',
            recommendedRadius: 3.0,
            description: 'Airport area with designated taxi stands'
        };
    } else if (isKowloon) {
        return {
            area: 'Kowloon',
            density: 'medium',
            recommendedRadius: 2.0,
            description: 'Urban area with good taxi stand coverage'
        };
    } else if (isHongKongIsland) {
        return {
            area: 'Hong Kong Island',
            density: 'medium',
            recommendedRadius: 2.0,
            description: 'Island area with good taxi stand coverage'
        };
    } else if (isNewTerritories) {
        return {
            area: 'New Territories',
            density: 'low',
            recommendedRadius: 4.0,
            description: 'Less dense area, may need larger search radius'
        };
    } else {
        return {
            area: 'Hong Kong',
            density: 'medium',
            recommendedRadius: 2.5,
            description: 'General Hong Kong area'
        };
    }
};

// Get detailed spatial information for debugging
export const getSpatialInfo = (lat: number, lon: number) => {
    const context = getLocationContext(lat, lon);
    const boundingBox = createBoundingBox(lat, lon, context.recommendedRadius);
    
    return {
        location: { lat, lon },
        context,
        boundingBox,
        spatialFilter: createSpatialFilter(
            lat, 
            lon, 
            context.recommendedRadius, 
            COORDINATE_SYSTEMS.HK1980,
            15
        )
    };
};

// Test function to verify spatial calculation (for development)
export const testSpatialCalculation = (lat: number, lon: number) => {
    console.log('🧪 Testing Spatial Calculation');
    console.log('================================');
    
    const context = getLocationContext(lat, lon);
    const boundingBox = createBoundingBox(lat, lon, context.recommendedRadius);
    const spatialFilter = createSpatialFilter(
        lat, 
        lon, 
        context.recommendedRadius, 
        COORDINATE_SYSTEMS.HK1980,
        15
    );
    
    console.log('📍 Location:', { lat, lon });
    console.log('🏙️ Area:', context.area);
    console.log('📊 Density:', context.density);
    console.log('🎯 Recommended Radius:', context.recommendedRadius, 'km');
    console.log('📝 Description:', context.description);
    console.log('📦 Bounding Box:', boundingBox);
    console.log('🔍 Spatial Filter:', spatialFilter);
    
    return {
        location: { lat, lon },
        context,
        boundingBox,
        spatialFilter
    };
};

// Test function specifically for Kwun Tong area
export const testKwunTongLocation = () => {
    console.log('🧪 Testing Kwun Tong Location Detection');
    console.log('=======================================');
    
    // Kwun Tong coordinates (example)
    const kwunTongLat = 22.3125;
    const kwunTongLon = 114.225;
    
    const context = getLocationContext(kwunTongLat, kwunTongLon);
    const optimalRadius = calculateOptimalRadius(kwunTongLat, kwunTongLon);
    
    console.log('📍 Kwun Tong Coordinates:', { lat: kwunTongLat, lon: kwunTongLon });
    console.log('🏙️ Detected Area:', context.area);
    console.log('📊 Density:', context.density);
    console.log('🎯 Recommended Radius:', context.recommendedRadius, 'km');
    console.log('🔧 Optimal Radius:', optimalRadius, 'km');
    console.log('📝 Description:', context.description);
    
    // Test if Kwun Tong is properly detected
    const isKwunTongDetected = context.area === 'Kwun Tong';
    console.log('✅ Kwun Tong Detection:', isKwunTongDetected ? 'SUCCESS' : 'FAILED');
    
    return {
        location: { lat: kwunTongLat, lon: kwunTongLon },
        context,
        optimalRadius,
        isKwunTongDetected
    };
};

// Test 500m radius search specifically
export const test500mRadius = async (userLat: number, userLon: number): Promise<TaxiStandsResponse> => {
    console.log('🧪 Testing 500m Radius Search');
    console.log('=============================');
    console.log(`📍 User Location: (${userLat}, ${userLon})`);
    
    try {
        const response = await axios.get(TAXI_STANDS_WFS_URL, {
            params: {
                service: 'wfs',
                request: 'GetFeature',
                typenames: 'Taxi_Stands',
                outputFormat: 'geojson',
                maxFeatures: MAX_TAXI_STANDS_FEATURES,
                srsName: 'EPSG:4326'
            },
            timeout: 10000
        });

        console.log('📡 API Response Status:', response.status);
        console.log('🔢 Total Features:', response.data?.features?.length || 0);

        if (response.data && response.data.features) {
            const allStands: TaxiStand[] = response.data.features.map((feature: any) => {
                const lat = feature.geometry.coordinates[1];
                const lon = feature.geometry.coordinates[0];
                const distance = calculateDistance(userLat, userLon, lat, lon);

                return {
                    id: feature.properties.GmlID || feature.properties.OBJECTID?.toString(),
                    location: { latitude: lat, longitude: lon },
                    region: {
                        en: feature.properties.Region_EN || '',
                        tc: feature.properties.Region_TC || '',
                        sc: feature.properties.Region_SC || ''
                    },
                    district: {
                        en: feature.properties.District_EN || '',
                        tc: feature.properties.District_TC || '',
                        sc: feature.properties.District_SC || ''
                    },
                    locationName: {
                        en: feature.properties.Location_EN || '',
                        tc: feature.properties.Location_TC || '',
                        sc: feature.properties.Location_SC || ''
                    },
                    status: {
                        en: feature.properties.Status_EN || '',
                        tc: feature.properties.Status_TC || '',
                        sc: feature.properties.Status_SC || ''
                    },
                    distance: distance
                };
            });

            // Filter for stands within 500m
            const standsWithin500m = allStands
                .filter(stand => (stand.distance || 0) <= 500)
                .sort((a, b) => (a.distance || 0) - (b.distance || 0));

            console.log(`📊 Total stands processed: ${allStands.length}`);
            console.log(`🎯 Stands within 500m: ${standsWithin500m.length}`);
            
            if (standsWithin500m.length > 0) {
                console.log('🏃 Stands within 500m:');
                standsWithin500m.forEach((stand, index) => {
                    console.log(`  ${index + 1}. ${stand.locationName.en} - ${Math.round(stand.distance || 0)}m`);
                });
            } else {
                console.log('⚠️ No stands within 500m found');
                const closest5 = allStands
                    .sort((a, b) => (a.distance || 0) - (b.distance || 0))
                    .slice(0, 5);
                console.log('🔍 Closest 5 stands:');
                closest5.forEach((stand, index) => {
                    console.log(`  ${index + 1}. ${stand.locationName.en} - ${Math.round(stand.distance || 0)}m`);
                });
            }

            return {
                success: true,
                data: standsWithin500m.length > 0 ? standsWithin500m : allStands.slice(0, 10)
            };
        }

        return {
            success: false,
            data: [],
            error: 'No features in API response'
        };

    } catch (error) {
        console.error('❌ 500m Radius Test Error:', error);
        return {
            success: false,
            data: [],
            error: error instanceof Error ? error.message : '500m radius test failed'
        };
    }
};

// Test basic API without spatial filtering
export const testBasicAPI = async (): Promise<TaxiStandsResponse> => {
    console.log('🧪 Testing Basic API (No Spatial Filter)');
    console.log('==========================================');
    
    try {
        const response = await axios.get(TAXI_STANDS_WFS_URL, {
            params: {
                service: 'wfs',
                request: 'GetFeature',
                typenames: 'Taxi_Stands',
                outputFormat: 'geojson',
                maxFeatures: MAX_TAXI_STANDS_FEATURES,
                srsName: 'EPSG:4326'
            },
            timeout: 10000
        });

        console.log('📡 Basic API Response Status:', response.status);
        console.log('📄 Basic API Response Data:', response.data);
        console.log('🔢 Basic API Features Count:', response.data?.features?.length || 0);

        if (response.data && response.data.features) {
            const taxiStands: TaxiStand[] = response.data.features.map((feature: any) => ({
                id: feature.properties.GmlID || feature.properties.OBJECTID?.toString(),
                location: {
                    latitude: feature.geometry.coordinates[1],
                    longitude: feature.geometry.coordinates[0]
                },
                region: {
                    en: feature.properties.Region_EN || '',
                    tc: feature.properties.Region_TC || '',
                    sc: feature.properties.Region_SC || ''
                },
                district: {
                    en: feature.properties.District_EN || '',
                    tc: feature.properties.District_TC || '',
                    sc: feature.properties.District_SC || ''
                },
                locationName: {
                    en: feature.properties.Location_EN || '',
                    tc: feature.properties.Location_TC || '',
                    sc: feature.properties.Location_SC || ''
                },
                status: {
                    en: feature.properties.Status_EN || '',
                    tc: feature.properties.Status_TC || '',
                    sc: feature.properties.Status_SC || ''
                }
            }));

            console.log('✅ Basic API Success - Found', taxiStands.length, 'taxi stands');
            return {
                success: true,
                data: taxiStands
            };
        }

        return {
            success: false,
            data: [],
            error: 'No features in basic API response'
        };

    } catch (error) {
        console.error('❌ Basic API Error:', error);
        return {
            success: false,
            data: [],
            error: error instanceof Error ? error.message : 'Basic API failed'
        };
    }
};

// Format distance for display
export const formatDistance = (distanceMeters: number): string => {
    if (distanceMeters < 1000) {
        return `${Math.round(distanceMeters)}m`;
    } else {
        return `${(distanceMeters / 1000).toFixed(1)}km`;
    }
};
